import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  Typography,
  MenuItem,
  Alert,
} from "@mui/material";
import useAdd from "../../api/useAdd";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";
import { getUniqueCurrencies } from "../../utils/currencyOptions";

const DebtorsAdd = ({ open, onClose, token, onSuccess }) => {
  const { t } = useTranslation();
  const { handleAdd, loading, resetForm } = useAdd(
    "shop/customer-debtors",
    token
  );

  const { data: shopsData, isLoading: shopLoading } = useFetchObjects(
    ["shop", "debtor-add"],
    "shop/shop",
    token
  );
  const shops = Array.isArray(shopsData) ? shopsData : shopsData?.results || [];
  const [selectedShop, setSelectedShop] = useState("");

  const { data: customersData, loading: customersLoading } = useFetchObjects(
    ["customers", "debtor-add", selectedShop],
    selectedShop ? `shop/customers?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currency", "debtor-add", selectedShop],
    selectedShop ? `shop/currencies?page_size=all&shop=${selectedShop}` : "",
    token
  );

  const customers = customersData?.results || customersData || [];
  const currencies = getUniqueCurrencies(
    currenciesData?.results || currenciesData || []
  );

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [exchangeRate, setExchangeRate] = useState("");
  const [loanDate, setLoanDate] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState("");

  const { data: accountsData, isLoading: accountLoading } = useFetchObjects(
    ["accounts", "debtor-add", selectedShop],
    selectedShop ? `shop/accounts/?page_size=all&shop=${selectedShop}` : "",
    token
  );
  const accounts = Array.isArray(accountsData)
    ? accountsData
    : accountsData?.results || [];

  const selectedCurrencyId = selectedCurrency?.id
    ? String(selectedCurrency.id)
    : "";
  const shopAccounts = useMemo(() => accounts, [accounts]);
  const selectedAccount = useMemo(
    () =>
      shopAccounts.find((a) => String(a.id) === String(selectedAccountId)) ||
      null,
    [shopAccounts, selectedAccountId]
  );

  useEffect(() => {
    if (accountLoading || !selectedAccountId) return;
    if (!selectedCurrencyId) {
      setSelectedAccountId("");
      return;
    }
    if (shopAccounts.length === 0) return;
    const exists = shopAccounts.some(
      (a) => String(a.id) === String(selectedAccountId)
    );
    if (!exists) {
      setSelectedAccountId("");
    }
  }, [accountLoading, selectedCurrencyId, shopAccounts, selectedAccountId]);

  const { data: directRateData } = useFetchObjects(
    [
      "debtor-rate-direct",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedCurrencyId}&to_currency=${selectedAccount.currency}`
      : "",
    token
  );
  const { data: reverseRateData } = useFetchObjects(
    [
      "debtor-rate-reverse",
      selectedShop,
      selectedCurrencyId,
      selectedAccount?.currency,
    ],
    selectedShop && selectedCurrencyId && selectedAccount?.currency
      ? `shop/exchange-rates/?page_size=1&shop=${selectedShop}&from_currency=${selectedAccount.currency}&to_currency=${selectedCurrencyId}`
      : "",
    token
  );

  const exchangeRatePreview = useMemo(() => {
    if (!selectedCurrencyId) return "";
    const accountCurrencyId = selectedAccount?.currency ?? selectedCurrencyId;
    if (String(selectedCurrencyId) === String(accountCurrencyId)) return "1";
    const direct = directRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = reverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [selectedCurrencyId, selectedAccount, directRateData, reverseRateData]);

  const convertedAmountPreview = useMemo(() => {
    const nAmount = Number(amount);
    const nRate = Number(exchangeRate);
    if (
      !Number.isFinite(nAmount) ||
      !Number.isFinite(nRate) ||
      nAmount <= 0 ||
      nRate <= 0
    ) {
      return "";
    }
    return (nAmount * nRate).toFixed(2);
  }, [amount, exchangeRate]);

  useEffect(() => {
    setExchangeRate(exchangeRatePreview);
  }, [exchangeRatePreview]);

  const handleSubmit = () => {
    if (
      !selectedShop ||
      !selectedCustomer?.id ||
      !selectedCurrency?.id ||
      !amount
    )
      return;
    if (selectedAccountId && !exchangeRate) return;
    handleAdd({
      customer: selectedCustomer.id,
      currency: selectedCurrency.id,
      account: selectedAccountId || null,
      amount: Number(amount),
      exchange_rate: selectedAccountId ? exchangeRate : exchangeRate || 1,
      loan_date: loanDate,
      notes: notes.trim() || undefined,
    });
  };

  useEffect(() => {
    if (resetForm) {
      setSelectedShop("");
      setSelectedCustomer(null);
      setSelectedCurrency(null);
      setSelectedAccountId("");
      setAmount("");
      setExchangeRate("");
      setLoanDate(new Date().toISOString().slice(0, 16));
      setNotes("");
      onSuccess?.();
      onClose();
    }
  }, [resetForm, onClose, onSuccess]);

  const currentBalance = selectedCustomer?.total_owed_by_currency;
  const balanceStr =
    currentBalance && typeof currentBalance === "object"
      ? Object.entries(currentBalance)
          .map(([cur, amt]) => `${Number(amt).toFixed(2)} ${cur}`)
          .join(", ") || "0"
      : "0";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {t("debtor.addTitle", "Give money to customer (Debtors)")}
      </DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <Alert severity="warning" sx={{ mb: 1 }}>
            {t(
              "debtor.addWarning",
              "This increases the customer balance. If you select an account, the amount is also deducted from that account."
            )}
          </Alert>

          <TextField
            select
            margin="dense"
            label={t("debtor.selectShop", "Select branch")}
            fullWidth
            required
            value={selectedShop}
            onChange={(e) => {
              setSelectedShop(e.target.value);
              setSelectedCustomer(null);
              setSelectedCurrency(null);
              setSelectedAccountId("");
            }}
          >
            {shopLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              shops.map((s) => (
                <MenuItem key={s.id} value={String(s.id)}>
                  {s.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Autocomplete
            value={selectedCustomer}
            onChange={(e, newValue) => setSelectedCustomer(newValue)}
            options={selectedShop ? customers || [] : []}
            getOptionLabel={(option) => option.name || "—"}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            loading={customersLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("debtor.customer", "Customer")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {customersLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {selectedCustomer && (
            <Box mt={1} p={2} bgcolor="grey.100" borderRadius={1}>
              <Typography variant="body2" color="textSecondary">
                {t("debtor.currentBalance", "Current balance")}:{" "}
                <strong>{balanceStr}</strong>
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                {t(
                  "debtor.balanceIncreasedByLoan",
                  "This amount will be added to the customer balance."
                )}
              </Typography>
            </Box>
          )}

          <Autocomplete
            value={selectedCurrency}
            onChange={(e, newValue) => {
              setSelectedCurrency(newValue);
              setSelectedAccountId("");
            }}
            options={selectedShop ? currencies || [] : []}
            getOptionLabel={(option) => option.title || ""}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={currencyLoading}
            disabled={!selectedShop}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("debtor.selectCurrency", "Currency")}
                margin="dense"
                fullWidth
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {currencyLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <TextField
            select
            margin="dense"
            label={t("debtor.account", "Account")}
            fullWidth
            value={selectedAccountId}
            onChange={(e) => setSelectedAccountId(e.target.value)}
            disabled={!selectedShop || !selectedCurrencyId || accountLoading}
            helperText={t(
              "debtor.accountOptionalHint",
              "Optional. Select an account to deduct from it; leave empty to only increase customer balance."
            )}
          >
            <MenuItem value="">
              {t("debtor.noAccountSelected", "None (do not deduct from account)")}
            </MenuItem>
            {accountLoading ? (
              <MenuItem value="__loading" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : shopAccounts.length === 0 ? (
              <MenuItem value="" disabled>
                {t("debtor.noAccount", "No accounts found")}
              </MenuItem>
            ) : (
              shopAccounts.map((acc) => (
                <MenuItem key={acc.id} value={String(acc.id)}>
                  {acc.name} ({acc.currency_title || "-"})
                  {acc.balance != null ? ` — ${acc.balance}` : ""}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("debtor.amount", "Amount")}
            type="number"
            fullWidth
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {selectedAccountId && (
            <>
              <TextField
                margin="dense"
                label={t("debtor.exchangeRate", "Exchange Rate")}
                type="number"
                fullWidth
                required
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
                inputProps={{ min: 0, step: "0.000001" }}
              />

              <TextField
                margin="dense"
                label={t("debtor.convertedAmount", "Converted Amount")}
                fullWidth
                value={convertedAmountPreview}
                disabled
              />

              {!exchangeRatePreview &&
                selectedCurrencyId &&
                selectedAccountId &&
                !exchangeRate && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t(
                      "debtor.missingRate",
                      "No exchange rate found for selected account currency. You can enter it manually."
                    )}
                  </Alert>
                )}
            </>
          )}

          <TextField
            margin="dense"
            label={t("debtor.loanDate", "Date")}
            type="datetime-local"
            fullWidth
            required
            value={loanDate}
            onChange={(e) => setLoanDate(e.target.value)}
          />

          <TextField
            margin="dense"
            label={t("debtor.notes", "Notes")}
            multiline
            rows={2}
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t(
              "debtor.notesPlaceholder",
              "e.g. Customer took cash from Main AFN account"
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("debtor.cancel", "Cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading ||
            !selectedShop ||
            !selectedCustomer ||
            !selectedCurrency ||
            !amount ||
            (Boolean(selectedAccountId) && !exchangeRate)
          }
          sx={{ minWidth: 120 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("debtor.submit", "Save debtor")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DebtorsAdd;
